import * as THREE from "three";
import Orchestrator from "../../../Orchestrator.js";
import { columnFromX, columnFromZ } from "../../config.js";
import { WORLD_SIZE } from "../constants.js";
import { FEATURE_TYPES, defaultFeature, defaultParams } from "../features/index.js";
import { generateHeights } from "../generateHeights.js";
import shippedLayout from "../layout.json";
import {
    clearSavedLayout,
    cloneLayout,
    downloadLayout,
    normalizeLayout,
    saveLayout,
} from "./LayoutStore.js";
import MapView from "./MapView.js";

export default class TerrainEditor {
    constructor(terrain) {
        this.orc = new Orchestrator();
        this.terrain = terrain;
        this.layout = terrain.layout;
        this.selected = null;
        this.epoch = 0;
        this.previewId = 0;
        this.saveTimer = 0;
        this.rebuildTimer = 0;
        this._camDir = new THREE.Vector3();

        this.map = new MapView({
            onSelect: (feature) => this.select(feature),
            onEdit: () => this.onMapEdit(),
            onCommit: () => this.onMapCommit(),
        });
        this.map.setLayout(this.layout);
        this.map.setHeights(terrain.data.heights, WORLD_SIZE, WORLD_SIZE);

        this.edit = {
            type: "plains",
            x: 0,
            z: 0,
            sx: 80,
            sz: 60,
            rotationDeg: 0,
            height: 20,
            strength: 1,
            seed: 1,
            terraces: 4,
            meander: 0.35,
            frequency: 5,
        };
        this.addType = "plateau";

        const folder = this.orc.debug.ui.addFolder("Terrain");
        this.folder = folder;
        folder.add(this.layout, "seed", 0, 99999, 1).name("seed").onChange(() => this.changed());
        folder.add(this.layout.base, "height", 0, 80, 1).name("base height").onChange(() => this.changed());
        folder.add(this.layout.base, "amplitude", 0, 40, 0.5).name("base amplitude").onChange(() => this.changed());
        folder.add(this.layout.base, "scale", 0.0005, 0.02, 0.0001).name("base scale").onChange(() => this.changed());
        folder.add(this.layout.base, "octaves", 1, 8, 1).name("octaves").onChange(() => this.changed());
        folder.add(this.layout.spawn, "x", 0, WORLD_SIZE - 1, 1).name("spawn x").onChange(() => this.changed());
        folder.add(this.layout.spawn, "z", 0, WORLD_SIZE - 1, 1).name("spawn z").onChange(() => this.changed());
        folder.add(this, "addType", FEATURE_TYPES).name("new feature");
        folder.add(this, "addFeature").name("add feature");
        folder.add(this, "teleportToSpawn").name("teleport to spawn");
        folder.add(this, "exportLayout").name("export JSON");
        folder.add(this, "importLayout").name("import JSON");
        folder.add(this, "revertLayout").name("revert shipped");

        this.selectedFolder = folder.addFolder("Selected");
        this.selectedFolder.add(this.edit, "type", FEATURE_TYPES).name("type").onChange(() => this.writeEdit(true));
        this.selectedFolder.add(this.edit, "x", 0, WORLD_SIZE, 1).name("x").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "z", 0, WORLD_SIZE, 1).name("z").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "sx", 8, 500, 1).name("scale x").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "sz", 8, 500, 1).name("scale z").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "rotationDeg", -180, 180, 1).name("rotation").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "height", -40, 160, 1).name("height / depth").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "strength", 0, 2, 0.01).name("strength").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this.edit, "seed", 0, 99999, 1).name("seed").onChange(() => this.writeEdit(false));
        this.terraceControl = this.selectedFolder.add(this.edit, "terraces", 1, 8, 1).name("terraces").onChange(() => this.writeEdit(false));
        this.meanderControl = this.selectedFolder.add(this.edit, "meander", 0, 1, 0.01).name("meander").onChange(() => this.writeEdit(false));
        this.frequencyControl = this.selectedFolder.add(this.edit, "frequency", 1, 12, 1).name("frequency").onChange(() => this.writeEdit(false));
        this.selectedFolder.add(this, "duplicateFeature").name("duplicate");
        this.selectedFolder.add(this, "moveFeatureUp").name("move up");
        this.selectedFolder.add(this, "moveFeatureDown").name("move down");
        this.selectedFolder.add(this, "deleteFeature").name("delete");
        this.selectedFolder.add(this, "teleportToFeature").name("teleport here");
        this.selectedFolder.hide();

        const hint = document.getElementById("hint");
        if (hint && !hint.textContent.includes("地形图")) {
            hint.textContent += "　·　M 地形图";
        }
        this.map.setStatus("M 打开地图。导出的 layout.json 放到 src/ThreeJS/Stage/terrain/ 后即是正式地图。");
    }

    update() {
        if (!this.map.open) return;
        const robot = this.orc.stage.robot;
        const camera = this.orc.camera.instance;
        camera.getWorldDirection(this._camDir);
        this.map.setMarkers({
            robotI: robot.state.i,
            robotJ: robot.state.j,
            camX: columnFromX(camera.position.x),
            camZ: columnFromZ(camera.position.z),
            dirX: this._camDir.x,
            dirZ: this._camDir.z,
        });
        this.map.paint();
    }

    select(feature) {
        this.selected = feature;
        if (!feature) {
            this.selectedFolder.hide();
            this.map.setSelected(null);
            return;
        }
        this.pullEdit();
        this.selectedFolder.show();
        this.refreshParamVisibility();
        this.map.setSelected(feature.id);
        this.refreshControllers();
    }

    pullEdit() {
        const feature = this.selected;
        if (!feature) return;
        this.edit.type = feature.type;
        this.edit.x = Math.round(feature.x);
        this.edit.z = Math.round(feature.z);
        this.edit.sx = Math.round(feature.sx);
        this.edit.sz = Math.round(feature.sz);
        this.edit.rotationDeg = Math.round((feature.rotation * 180) / Math.PI);
        this.edit.height = feature.height;
        this.edit.strength = feature.strength ?? 1;
        this.edit.seed = feature.seed | 0;
        this.edit.terraces = feature.params?.terraces ?? 4;
        this.edit.meander = feature.params?.meander ?? 0.35;
        this.edit.frequency = feature.params?.frequency ?? 5;
        this.refreshControllers();
    }

    writeEdit(typeChanged) {
        const feature = this.selected;
        if (!feature) return;
        if (typeChanged && feature.type !== this.edit.type) {
            feature.type = this.edit.type;
            feature.params = { ...defaultParams(feature.type), ...feature.params };
            this.pullEdit();
        }
        feature.x = this.edit.x;
        feature.z = this.edit.z;
        feature.sx = this.edit.sx;
        feature.sz = this.edit.sz;
        feature.rotation = (this.edit.rotationDeg * Math.PI) / 180;
        feature.height = this.edit.height;
        feature.strength = this.edit.strength;
        feature.seed = this.edit.seed | 0;
        feature.params = feature.params || {};
        feature.params.terraces = this.edit.terraces;
        feature.params.meander = this.edit.meander;
        feature.params.frequency = this.edit.frequency;
        this.refreshParamVisibility();
        this.changed();
    }

    refreshParamVisibility() {
        const type = this.selected?.type;
        this.terraceControl.show(type === "plateau");
        this.meanderControl.show(type === "canyon");
        this.frequencyControl.show(type === "dunes");
    }

    refreshControllers() {
        this.selectedFolder.controllers.forEach((controller) => controller.updateDisplay());
        this.folder.controllers.forEach((controller) => controller.updateDisplay());
    }

    onMapEdit() {
        this.pullEdit();
        this.preview();
        this.scheduleSave();
    }

    onMapCommit() {
        this.pullEdit();
        this.changed();
    }

    changed() {
        this.map.setLayout(this.layout);
        this.preview();
        this.scheduleSave();
        this.scheduleRebuild();
    }

    preview() {
        const epoch = this.epoch;
        const id = ++this.previewId;
        window.setTimeout(() => {
            if (id !== this.previewId || epoch !== this.epoch) return;
            const result = generateHeights(this.layout, 256);
            if (id !== this.previewId || epoch !== this.epoch) return;
            this.map.setHeights(result.heights, result.resolution, this.layout.size);
        }, 40);
    }

    scheduleSave() {
        window.clearTimeout(this.saveTimer);
        this.saveTimer = window.setTimeout(() => saveLayout(this.layout), 200);
    }

    scheduleRebuild() {
        window.clearTimeout(this.rebuildTimer);
        this.rebuildTimer = window.setTimeout(() => this.rebuild(), 280);
    }

    rebuild() {
        const epoch = ++this.epoch;
        this.map.setStatus("正在生成地形…");
        this.terrain.setLayout(this.layout).then(() => {
            if (epoch !== this.epoch) return;
            this.reseatRobot();
            this.map.setHeights(this.terrain.data.heights, WORLD_SIZE, WORLD_SIZE);
            this.map.setStatus("地形已更新");
        });
    }

    reseatRobot() {
        const stage = this.orc.stage;
        const robot = stage.robot;
        robot.state.y = this.terrain.heightAt(robot.state.i, robot.state.j);
        robot.state.digs = 0;
        robot.snapToCell();
        stage.debris.clear();
    }

    addFeature() {
        const feature = defaultFeature(this.addType, this.map.centerX, this.map.centerZ);
        this.layout.features.push(feature);
        this.select(feature);
        this.changed();
    }

    duplicateFeature() {
        if (!this.selected) return;
        const copy = JSON.parse(JSON.stringify(this.selected));
        copy.id = `f${Date.now().toString(36)}`;
        copy.x = Math.min(WORLD_SIZE - 1, copy.x + 24);
        copy.z = Math.min(WORLD_SIZE - 1, copy.z + 24);
        this.layout.features.push(copy);
        this.select(copy);
        this.changed();
    }

    deleteFeature() {
        if (!this.selected) return;
        const index = this.layout.features.indexOf(this.selected);
        if (index >= 0) this.layout.features.splice(index, 1);
        this.select(null);
        this.changed();
    }

    moveFeatureUp() {
        this.moveSelected(1);
    }

    moveFeatureDown() {
        this.moveSelected(-1);
    }

    moveSelected(step) {
        if (!this.selected) return;
        const list = this.layout.features;
        const index = list.indexOf(this.selected);
        const next = index + step;
        if (index < 0 || next < 0 || next >= list.length) return;
        const [feature] = list.splice(index, 1);
        list.splice(next, 0, feature);
        this.changed();
    }

    teleportToSpawn() {
        this.teleport(this.layout.spawn.x, this.layout.spawn.z);
    }

    teleportToFeature() {
        if (!this.selected) return;
        this.teleport(this.selected.x, this.selected.z);
    }

    teleport(x, z) {
        const stage = this.orc.stage;
        const i = Math.max(0, Math.min(WORLD_SIZE - 1, Math.round(x)));
        const j = Math.max(0, Math.min(WORLD_SIZE - 1, Math.round(z)));
        stage.robot.state.i = i;
        stage.robot.state.j = j;
        stage.robot.state.y = this.terrain.heightAt(i, j);
        stage.robot.snapToCell();
        stage.placeCamera();
    }

    exportLayout() {
        downloadLayout(this.layout);
        this.map.setStatus("已下载 layout.json");
    }

    importLayout() {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/json";
        input.addEventListener("change", () => {
            const file = input.files?.[0];
            if (!file) return;
            file.text().then((text) => {
                try {
                    const layout = normalizeLayout(JSON.parse(text), shippedLayout);
                    this.replaceLayout(layout);
                    this.map.setStatus("已导入布局");
                } catch (error) {
                    console.warn(error);
                    this.map.setStatus("无法读取这个 JSON");
                }
            });
        });
        input.click();
    }

    revertLayout() {
        clearSavedLayout();
        this.replaceLayout(cloneLayout(shippedLayout));
        this.map.setStatus("已恢复内置地图");
    }

    replaceLayout(layout) {
        this.layout.seed = layout.seed;
        Object.assign(this.layout.base, layout.base);
        Object.assign(this.layout.spawn, layout.spawn);
        this.layout.features.splice(0, this.layout.features.length, ...layout.features);
        this.map.setLayout(this.layout);
        this.select(null);
        this.refreshControllers();
        this.changed();
    }
}
